import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, interval } from 'rxjs';
import { catchError, of, switchMap, takeWhile, tap } from 'rxjs';
import { SpaceRepository } from '../state/space.repository';
import { URLService } from './dynamic-url.service';

/**
 * State surfaced to the UI for the AI Session button.
 *
 * idle      - no LK dispatch is active. The orchestrator session may still
 *             exist (durable across LK dispatches by design), but from the
 *             user's perspective there's no agent in the call. Button is
 *             clickable to (re-)dispatch.
 * starting  - we just POSTed start; haven't seen status=ready yet
 * ready     - status endpoint reports both `running` AND `dispatched`
 * error     - the start call failed or the status endpoint is degraded
 *
 * NOTE: we previously had a 'partial' state for "running but not dispatched"
 * (orchestrator alive, LK agent gone). That state stuck around forever because
 * there was no auto-recovery loop, leaving the button looking broken between
 * dispatches. Treat that case as 'idle' — the user clicks once to recover.
 */
export type AiSessionUiState = 'idle' | 'starting' | 'ready' | 'error';

export interface AiSessionStatus {
  running: boolean;
  dispatched: boolean;
  messageCount?: number;
  lastMessageAt?: string;
  /** What THIS space's orchestrator advertises (ORCHESTRATOR_CONTRACT.md). A
   *  space can be routed to a self-hosted brain, so affordances are drawn from
   *  what that deployment says it can do rather than from what our own can. */
  capabilities?: string[];
}

/**
 * AI Session service: drives the AI Session button.
 *
 * Talks to pencil-api's /ai-session/:id/* proxy endpoints (Firebase auth is
 * added by the existing auth interceptor). pencil-api forwards to the
 * OpenClaw orchestrator under the hood — the FE never sees the bearer.
 *
 * Polling cadence: 5s while a session is starting/active and the panel is
 * visible. Subscribers stop polling automatically via the takeWhile guard.
 */
@Injectable({ providedIn: 'root' })
export class AiSessionService {
  private readonly stateSubject = new BehaviorSubject<AiSessionUiState>('idle');
  private readonly statusSubject = new BehaviorSubject<AiSessionStatus | null>(null);
  private pollSub?: Subscription;

  readonly state$: Observable<AiSessionUiState> = this.stateSubject.asObservable();
  readonly status$: Observable<AiSessionStatus | null> = this.statusSubject.asObservable();

  constructor(
    private http: HttpClient,
    private spaceRepo: SpaceRepository,
    private urlService: URLService,
  ) {}

  /**
   * Kick off an AI session for the current space. Idempotent: if pencil-api
   * already has one running, the POST is a no-op and we just begin polling.
   */
  startAiSession(): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      this.stateSubject.next('error');
      return;
    }

    this.stateSubject.next('starting');
    this.http
      .post(`${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/start`, {})
      .pipe(catchError(() => of(null)))
      .subscribe((res) => {
        if (!res) {
          this.stateSubject.next('error');
          return;
        }
        // Begin polling. The status endpoint is the source of truth.
        this.beginPolling(spaceId);
      });
  }

  /**
   * Bring the voice agent INTO the current space's call (green button). Fire-and-forget:
   * pencil-api dispatches the worker into `${spaceId}-MainRoom`. The button's visible
   * state is driven by the live room roster (livekit.agentInCall$), not this call — this
   * just requests the dispatch; the button flips to red when the agent actually joins.
   */
  joinAgentCall(): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return;
    }
    this.http
      .post(
        `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/agent-call/join`,
        {},
      )
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /**
   * Evict the voice agent from the current space's call (red button / end-call-for-
   * everyone). Fire-and-forget: pencil-api removes the agent participant + deletes the
   * dispatch. The orchestrator + text chat are untouched — only voice is pulled. Best-
   * effort: on failure the agent simply rides the LiveKit departure timeout instead.
   */
  leaveAgentCall(): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return;
    }
    this.http
      .post(
        `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/agent-call/leave`,
        {},
      )
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /**
   * Ask pencil-api to send the agent into the call this client just connected to, for
   * organizations that have "Join calls automatically" on (Settings → AI coworker).
   * Fire-and-forget: pencil-api checks the setting and joins only once per call, so
   * every client asks, and one that connects after the agent was sent away with the
   * red button doesn't bring it back.
   */
  autoJoinAgentCall(): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return;
    }
    this.http
      .post(
        `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/agent-call/auto-join`,
        {},
      )
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  /**
   * Ensure a LiveKit agent is actually in the call for the current space.
   *
   * Safe to call on EVERY panel open. It checks live status first and only
   * (re)dispatches when there is no active agent — so it brings the agent online
   * the first time AND re-summons it after it has left (call ended, user
   * re-joined, or the LK job exited on an empty room), WITHOUT evicting/respawning
   * a healthy session. This replaces the old one-shot `hasBroughtAgentOnline`
   * guard in the buttons, which never re-dispatched after the agent left because
   * the post-departure state is `idle`, not `error`.
   */
  ensureAgentInCall(): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      this.stateSubject.next('error');
      return;
    }
    this.http
      .get<AiSessionStatus & { ok: boolean; degraded?: boolean }>(
        `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/status`,
      )
      .pipe(catchError(() => of(null)))
      .subscribe((status) => {
        const live =
          !!status &&
          !(status as { degraded?: boolean }).degraded &&
          status.running &&
          status.dispatched;
        if (live) {
          // Agent already in the call — reflect it and resume polling, no POST
          // (avoids the evict/respawn churn the old guard was protecting against).
          this.statusSubject.next(status as AiSessionStatus);
          this.stateSubject.next('ready');
          this.beginPolling(spaceId);
        } else {
          // No active agent (idle / error / no session yet) — (re)dispatch.
          this.startAiSession();
        }
      });
  }

  /**
   * Poll status. Called automatically after a successful start, and can be
   * called by the panel on first load to discover an existing session.
   */
  beginPolling(spaceId: string): void {
    this.pollSub?.unsubscribe();
    this.pollSub = interval(5_000)
      .pipe(
        switchMap(() =>
          this.http
            .get<AiSessionStatus & { ok: boolean; degraded?: boolean }>(
              `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/status`,
            )
            .pipe(catchError(() => of({ ok: false, degraded: true } as any))),
        ),
        tap((status) => {
          if (!status || status.degraded) {
            this.stateSubject.next('error');
            return;
          }
          this.statusSubject.next(status);
          if (status.running && status.dispatched) {
            this.stateSubject.next('ready');
          } else {
            // No active LK agent → idle (clickable). Includes the case where
            // the orchestrator session is still alive but the LK dispatch
            // timed out (room emptied) — that's normal, not an error.
            this.stateSubject.next('idle');
          }
        }),
        // Cap polling to 5 min — if nothing's happened in 5 min, the user
        // probably navigated away. They can re-trigger from the button.
        takeWhile((_, i) => i < 60),
      )
      .subscribe();
  }

  stopPolling(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
  }

  /** Cheap status check on panel mount — discovers existing sessions without polling. */
  checkStatusOnce(): void {
    const spaceId = this.spaceRepo.activeSpaceId;
    if (!spaceId) {
      return;
    }
    this.http
      .get<AiSessionStatus & { ok: boolean; degraded?: boolean }>(
        `${this.urlService.getDynamicUrl()}/ai-session/${spaceId}/status`,
      )
      .pipe(catchError(() => of(null)))
      .subscribe((status) => {
        if (!status || (status as { degraded?: boolean }).degraded) {
          return;
        }
        this.statusSubject.next(status);
        if (status.running && status.dispatched) {
          this.stateSubject.next('ready');
          this.beginPolling(spaceId);
        }
        // If no active dispatch, stay 'idle' (the initial state). Don't even
        // begin polling — the user will click to start if they want a session.
      });
  }
}
